import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import BaseModal from 'components/common/modals/BaseModal'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import Actions from 'actions'
import { trackEvent } from 'utils/analyticsUtils'
import Checkmark from 'components/common/icons/bulk-invite/checkmark'
import Error from 'components/common/icons/bulk-invite/error'
import BulkCreatePatientsFileInput from './BulkCreatePatientsFileInput'

const useStyles = makeStyles({
  root: {
    width: '560px!important'
  },
  inputContainer: {
    padding: '18px 38px 8px 38px'
  },
  divider: {
    background: 'var(--bg-color-14)',
    width: '100%',
    height: 1,
    margin: '24px 12px'
  },
  errorsContainer: {
    padding: '0 12px',
    maxHeight: 360,
    overflowY: 'auto'
  },
  summaryContainer: {
    padding: '0 12px'
  },
  link: {
    color: 'var(--text-color-3)',
    textDecoration: 'underline'
  }
})

const BulkCreatePatientsDialog = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isOpen, status, errors, failureType, uploadId, summary, validationErrorCsvUrl, fileName } = useSelector(
    state => state.patientsReducer.bulkCreatePatientsForm
  )
  const [selectedFile, setSelectedFile] = useState()
  const [isConfirmCreateOpen, setIsConfirmCreateOpen] = useState(false)

  const isValidated = status === 'validated'
  const isCreating = status === 'creating'
  const isValidating = status === 'validating'
  const isRecovering = status === 'recovering'
  const isSubmitted = status === 'submitted'

  // Only blame the CSV when the CSV is actually what failed. An upload or submit failure that shows
  // "Some fields are incorrect..." sends the doctor hunting through valid rows for a problem that is
  // ours, which is what hid the missing media authorization for bulkCreatePatients paths.
  const failureCopyKeys = useMemo(() => {
    switch (failureType) {
      case 'upload':
        return { title: 'titleUploadFailed', message: 'uploadErrorMessage', primary: 'tryAgain' }
      case 'submit':
        return { title: 'titleSubmitFailed', message: 'submitErrorMessage', primary: 'tryAgain' }
      default:
        return { title: 'titleFailed', message: 'errorMessage', primary: 'replaceCsv' }
    }
  }, [failureType])

  const handleFileSelected = useCallback(
    file => {
      if (isCreating || isValidating || isRecovering) {
        return
      }

      setSelectedFile(file)
      if (status) {
        dispatch(Actions.resetBulkCreatePatientsForm())
      }
    },
    [dispatch, isCreating, isRecovering, isValidating, status]
  )

  const handleValidate = useCallback(() => {
    dispatch(Actions.validateBulkCreatePatientsForm({ file: selectedFile.file }))
  }, [dispatch, selectedFile])

  const handleCreate = useCallback(() => {
    setIsConfirmCreateOpen(true)
  }, [])

  const handleCancelCreate = useCallback(() => {
    setIsConfirmCreateOpen(false)
  }, [])

  const handleConfirmCreate = useCallback(() => {
    setIsConfirmCreateOpen(false)
    dispatch(Actions.submitBulkCreatePatients({ uploadId }))
  }, [dispatch, uploadId])

  const handleClose = useCallback(() => {
    setIsConfirmCreateOpen(false)
    dispatch(Actions.toggleBulkCreatePatientsPopup({ isOpen: false }))
  }, [dispatch])

  const handleReplaceFile = useCallback(() => {
    setIsConfirmCreateOpen(false)
    setSelectedFile(null)
    dispatch(Actions.resetBulkCreatePatientsForm())
  }, [dispatch])

  const prevIsOpenRef = useRef(isOpen)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    if (wasOpen !== isOpen) {
      trackEvent(`bulk create patients - dialog - ${isOpen ? 'open' : 'close'}`)
    }
    prevIsOpenRef.current = isOpen
    if (!isOpen) {
      setIsConfirmCreateOpen(false)
      setSelectedFile(null)
    }
  }, [isOpen])

  const title = useMemo(() => {
    if (isSubmitted) {
      return (
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Checkmark />
          </Grid>
          <Grid item>{t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.titleSubmitted')}</Grid>
        </Grid>
      )
    }

    if (status === 'fail') {
      return (
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Error />
          </Grid>
          <Grid item>
            {t(`pages.accountSettings.advancedActions.bulkCreatePatients.dialog.${failureCopyKeys.title}`)}
          </Grid>
        </Grid>
      )
    }

    return t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.title')
  }, [failureCopyKeys, isSubmitted, status, t])

  if (isSubmitted) {
    return (
      <BaseModal
        className={classes.root}
        open={isOpen}
        variant="alert"
        handleClose={handleClose}
        onPrimaryBtnClick={handleClose}
        primaryLabel={t('general.close')}
        largerButtons
        title={title}
      >
        <DazzedParagraph14 strong textAlign="center">
          {t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.submittedText')}
        </DazzedParagraph14>
        {uploadId && (
          <DazzedParagraph14 textAlign="center" style={{ marginTop: 8, textAlign: 'center', width: '100%' }}>
            {t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.uploadIdText', { uploadId })}
          </DazzedParagraph14>
        )}
      </BaseModal>
    )
  }

  if (status === 'fail') {
    return (
      <BaseModal
        className={classes.root}
        open={isOpen}
        variant="alert"
        handleClose={handleClose}
        onPrimaryBtnClick={handleReplaceFile}
        primaryLabel={t(`pages.accountSettings.advancedActions.bulkCreatePatients.dialog.${failureCopyKeys.primary}`)}
        secondaryLabel={t('general.close')}
        onSecondaryBtnClick={handleClose}
        largerButtons
        title={title}
      >
        <Grid container direction="column">
          <Grid item>
            <DazzedParagraph14 strong textAlign="center">
              {t(`pages.accountSettings.advancedActions.bulkCreatePatients.dialog.${failureCopyKeys.message}`)}
            </DazzedParagraph14>
          </Grid>
          {validationErrorCsvUrl && (
            <Grid item>
              <DazzedParagraph14 strong textAlign="center" style={{ marginTop: 8, width: '100%' }}>
                <a href={validationErrorCsvUrl} target="_blank" rel="noreferrer" className={classes.link}>
                  {t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.downloadValidationErrors')}
                </a>
              </DazzedParagraph14>
            </Grid>
          )}
          <Grid item>
            <div className={classes.divider} />
          </Grid>
          <Grid item>
            <Grid container className={classes.errorsContainer}>
              {errors.form && (
                <Grid item xs={12}>
                  <DazzedParagraph14 bold>
                    {t(`pages.accountSettings.advancedActions.bulkCreatePatients.errorCodes.${errors.form}`, {
                      defaultValue: errors.form
                    })}
                  </DazzedParagraph14>
                </Grid>
              )}
              {errors.rows?.map((row, i) => (
                <Grid item key={i} xs={12}>
                  <DazzedParagraph14 bold>
                    {t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.line', {
                      index: row.rowNumber || row.index + 2
                    })}
                  </DazzedParagraph14>
                  <ul>
                    {row.errors?.map(error => (
                      <li key={`${i}-${error.fieldName}-${error.error}`}>
                        <DazzedParagraph14 strong>
                          {t(`pages.accountSettings.advancedActions.bulkCreatePatients.fields.${error.fieldName}`)}
                          &nbsp;-&nbsp;
                          {t(`pages.accountSettings.advancedActions.bulkCreatePatients.errorCodes.${error.error}`)}
                        </DazzedParagraph14>
                      </li>
                    ))}
                  </ul>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </BaseModal>
    )
  }

  return (
    <>
      <BaseModal
        className={classes.root}
        open={isOpen}
        title={title}
        handleClose={handleClose}
        variant="alert"
        primaryLabel={
          isValidated
            ? t('pages.accountSettings.advancedActions.bulkCreatePatients.createButton')
            : t('pages.accountSettings.advancedActions.bulkCreatePatients.validateButton')
        }
        onPrimaryBtnClick={isValidated ? handleCreate : handleValidate}
        isPrimaryDisabled={isCreating || isValidating || isRecovering || (!isValidated && !selectedFile)}
        isLoading={isCreating || isValidating || isRecovering}
        secondaryLabel={
          isValidated
            ? t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.replaceCsv')
            : t('general.cancel')
        }
        onSecondaryBtnClick={isValidated ? handleReplaceFile : handleClose}
      >
        <Grid container alignItems="center" justifyContent="center" className={classes.inputContainer}>
          <Grid item xs={12}>
            <BulkCreatePatientsFileInput
              onFileUpload={handleFileSelected}
              selectedFileName={selectedFile?.fileName || fileName}
              disabled={isCreating || isValidating || isRecovering}
            />
          </Grid>
          {isValidated && summary && (
            <Grid item xs={12} className={classes.summaryContainer}>
              <div className={classes.divider} />
              <DazzedParagraph14 strong textAlign="center">
                {t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.validatedText', {
                  count: summary.rowCount
                })}
              </DazzedParagraph14>
            </Grid>
          )}
        </Grid>
      </BaseModal>
      <BaseModal
        className={classes.root}
        open={isOpen && isConfirmCreateOpen}
        title={t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.confirmCreateTitle')}
        handleClose={handleCancelCreate}
        variant="alert"
        primaryLabel={t('pages.accountSettings.advancedActions.bulkCreatePatients.createButton')}
        onPrimaryBtnClick={handleConfirmCreate}
        isPrimaryDisabled={isCreating || !uploadId}
        secondaryLabel={t('general.cancel')}
        onSecondaryBtnClick={handleCancelCreate}
        largerButtons
      >
        <DazzedParagraph14 strong textAlign="center">
          {t('pages.accountSettings.advancedActions.bulkCreatePatients.dialog.confirmCreate', {
            count: summary?.rowCount || 0
          })}
        </DazzedParagraph14>
      </BaseModal>
    </>
  )
}

export default BulkCreatePatientsDialog
